import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DateRange } from '@mui/x-date-pickers-pro';
import type { Dayjs } from 'dayjs';
import { useGetGroupedHistogram } from '../../apiHooks/queries/graphs/useGetGroupedHistogram';
import { GROUPED_AD_HISTOGRAM } from '../../constants/pageIdentifiers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface GroupedBarChartInterface {
  id: string;
  adManagementFilterDateRange: DateRange<Dayjs>;
}

interface VisibilityState {
  [key: string]: boolean;
}

export const GroupedBarChart: React.FC<GroupedBarChartInterface> = (props) => {
  const { data: groupedHistogramData } = useGetGroupedHistogram(
    GROUPED_AD_HISTOGRAM,
    props.id,
    props.adManagementFilterDateRange[0],
    props.adManagementFilterDateRange[1],
  );

  const labels: string[] | undefined = groupedHistogramData?.data?.data?.labels;
  const initialVisibility: VisibilityState = { 'Earned Income': true, 'Lost Income': true, 'Potential Income': true };

  const [visibility, setVisibility] = useState<VisibilityState>(initialVisibility);

  const toggleDataset = (datasetLabel: string) => {
    setVisibility((prev) => ({
      ...prev,
      [datasetLabel]: !prev[datasetLabel],
    }));
  };

  const visibleCount = Object.values(visibility).filter(Boolean).length;

  const barThickness = visibleCount === 3 ? 0.5 : visibleCount === 2 ? 0.6 : 1;
  const categoryThickness = visibleCount === 3 ? 0.3 : visibleCount === 2 ? 0.5 : 1;
  const borderRadius = visibleCount === 3 ? 100 : visibleCount === 2 ? 50 : 10;

  const options: ChartOptions<'bar'> = {
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        font: {
          size: 18,
          family: 'Poppins',
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 500,
      easing: 'easeInOutQuart',
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        border: {
          dash: [2, 2],
        },
        grid: {
          display: true,
        },
        beginAtZero: true,
        suggestedMax: 7,
      },
    },
  };

  const groupedData: ChartData<'bar'> = {
    labels,
    datasets: [
      visibility['Earned Income'] && {
        label: 'Earned Income',
        data: labels?.map((_, index) => groupedHistogramData?.data?.data?.dataSets?.earned[index] || 0) ?? [],
        backgroundColor: '#4C956C' as const,
        stack: 'Stack 0',
        borderRadius,
        barPercentage: barThickness,
        categoryPercentage: categoryThickness,
      },
      visibility['Potential Income'] && {
        label: 'Potential Income',
        data: labels?.map((_, index) => groupedHistogramData?.data?.data?.dataSets?.potentialIncome[index] || 0) ?? [],
        backgroundColor: '#FF9F1C' as const,
        stack: 'Stack 1',
        borderRadius,
        barPercentage: barThickness,
        categoryPercentage: categoryThickness,
      },
      visibility['Lost Income'] && {
        label: 'Lost Income',
        data: labels?.map((_, index) => groupedHistogramData?.data?.data?.dataSets?.lostIncome[index] || 0) ?? [],
        backgroundColor: '#DB3A34' as const,
        stack: 'Stack 2',
        borderRadius,
        barPercentage: barThickness,
        categoryPercentage: categoryThickness,
      },
    ].filter(Boolean) as ChartData<'bar'>['datasets'],
  };

  return (
    <div className="flex" style={{ height: '200px' }}>
      <div style={{ width: 'calc(100% - 48px) ', minWidth: '0' }}>
        <Bar options={options} data={groupedData} />
      </div>
      <div className="flex min-w-[160px] flex-col justify-start gap-2 px-4 pb-6 pt-2">
        {Object.keys(initialVisibility).map((datasetLabel) => (
          <button
            key={datasetLabel}
            onClick={() => toggleDataset(datasetLabel)}
            className={`flex h-[42px] w-full min-w-[130px] items-center gap-2 rounded-xl border py-2 pl-4 pr-2 text-[10px] outline-none focus:outline-none ${
              visibility[datasetLabel] ? 'bg-white text-primary' : 'text-black'
            } transition-all duration-300`}
          >
            <div
              className="h-2 w-2 rounded-full"
              style={{
                backgroundColor: visibility[datasetLabel]
                  ? (groupedData.datasets.find((d) => d?.label === datasetLabel)?.backgroundColor as string) || 'gray'
                  : 'gray',
              }}
            />
            {datasetLabel}
          </button>
        ))}
      </div>
    </div>
  );
};
